import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Body1,
  Button,
  Caption1,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  Switch,
  Textarea,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { ActionBar } from '../components/ActionBar';
import { Section } from '../components/Section';
import { useResponsiveStyles } from '../hooks/useResponsiveStyles';
import { SPACE, SPACE_X } from '../styles/scale';
import { useLayoutStyles } from '../styles/surfaces';
import {
  getNotificationTemplates,
  sendTestNotification,
  updateNotificationTemplates,
  type NotificationTemplate,
  type NotificationTemplateContent,
  type NotificationTemplateName,
  type NotificationTemplatesUpdate,
  type SendTestNotificationResult,
} from '../api/admin';

/**
 * Notification templates (#49, wireframe sheet 06.1 A2; #237 added the third).
 *
 * Three templates, fixed: request approved, PR created, and rejection. There
 * is deliberately no "add template" — this screen renders whatever
 * `GET /api/admin/notification-templates` returns, in a data-driven loop, so
 * a new entry in the backend's `NOTIFICATION_TEMPLATES` registry needs no
 * change here at all (confirmed when `approved` was added for #237) — but an
 * administrator still cannot invent an arbitrary new template of their own;
 * the set is closed to whatever the backend registry declares.
 *
 * Kept out of `AdminPanel.tsx` because that file is already four sections long;
 * this is the fifth, and it carries its own editing state, validation and two
 * network calls of its own.
 *
 * ── The design-system pass (#202) ──
 * The screen is a `Section` (`<h2>`) holding one `Section level={3}` (`<h3>`)
 * per template, so the two editors are navigable headings rather than a pair of
 * `Subtitle2` spans on a hand-rolled card — this file previously declared its
 * own `1px solid`/`borderRadiusMedium`/`16px` card, one of the fourteen
 * competing card definitions the shared surfaces replaced. The outer Section
 * passes `surface={false}` because its content is already two cards, and E1
 * inside E1 reads as a raised panel rather than as content.
 */

/** `{{tokenName}}` — the same syntax `lib/notificationTemplates.ts` validates. */
const TOKEN_PATTERN = /\{\{([^{}]*)\}\}/g;

/** Which of a template's two text fields a token click inserts into. */
type FieldName = 'subject' | 'body';

const useStyles = makeStyles({
  meta: { color: tokens.colorNeutralForeground2 },
  /** Stacked form fields inside one card — the 12px step, not the 16px page one. */
  fields: { display: 'flex', flexDirection: 'column', gap: SPACE.m },
  channels: { display: 'flex', gap: SPACE_X.s, alignItems: 'center', flexWrap: 'wrap' },
  tokens: { display: 'flex', gap: SPACE_X.xs, alignItems: 'center', flexWrap: 'wrap' },
});

type LoadState =
  | { phase: 'loading' }
  | { phase: 'ready'; templates: NotificationTemplate[] }
  | { phase: 'error'; message: string };

/**
 * Every `{{…}}` in the text that this template cannot substitute.
 *
 * Duplicated from the API deliberately, and only to give immediate feedback:
 * `PUT /api/admin/notification-templates` runs the same check and answers 400,
 * so a drift here is a worse error message, never a template that should have
 * been refused. The allow-list itself is not duplicated — it arrives with the
 * template.
 */
export function findUnknownTokens(text: string, allowed: readonly string[]): string[] {
  const found: string[] = [];
  for (const match of text.matchAll(TOKEN_PATTERN)) {
    const token = match[1].trim();
    if (allowed.includes(token) || found.includes(token)) continue;
    found.push(token);
  }
  return found;
}

function tokenErrorMessage(unknown: string[]): string | undefined {
  if (unknown.length === 0) return undefined;
  const list = unknown.map((token) => `{{${token}}}`).join(', ');
  return unknown.length === 1 ? `Unknown token ${list}` : `Unknown tokens: ${list}`;
}

type TestState =
  | { phase: 'idle' }
  | { phase: 'sending' }
  | { phase: 'sent'; result: SendTestNotificationResult }
  | { phase: 'error'; message: string };

/**
 * One template's editor: subject, body, the token list beneath them, the two
 * channel switches and its own Send test.
 */
function TemplateEditor({
  template,
  value,
  onChange,
  onSendTest,
  testState,
  showErrors,
}: {
  template: NotificationTemplate;
  value: NotificationTemplateContent;
  onChange: (next: NotificationTemplateContent) => void;
  onSendTest: () => void;
  testState: TestState;
  showErrors: boolean;
}) {
  const styles = useStyles();
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  /**
   * A token chip inserts into whichever field was last edited — the body by
   * default, which is where the tokens are listed and where all but one of them
   * belongs.
   */
  const [target, setTarget] = useState<FieldName>('body');

  const allowed = template.tokens.map((token) => token.name);
  const subjectError = tokenErrorMessage(findUnknownTokens(value.subject, allowed));
  const bodyError = tokenErrorMessage(findUnknownTokens(value.body, allowed));

  /**
   * Inserts `{{token}}` at the caret of the target field — not appended at the
   * end, and not merely displayed for copying: A2 note 1 makes clicking the way
   * a token is written, so nobody has to spell a merge field by hand.
   */
  function insertToken(tokenName: string) {
    const element = target === 'subject' ? subjectRef.current : bodyRef.current;
    const text = value[target];
    const snippet = `{{${tokenName}}}`;
    // jsdom and a real browser both give a caret on a focused field; when the
    // field has never been focused there is none, and appending is the only
    // sensible destination.
    const start = element?.selectionStart ?? text.length;
    const end = element?.selectionEnd ?? text.length;
    const next = `${text.slice(0, start)}${snippet}${text.slice(end)}`;
    onChange({ ...value, [target]: next });

    // Focus returns to the field with the caret after what was just inserted,
    // so a second token can be typed or clicked straight away.
    const caret = start + snippet.length;
    requestAnimationFrame(() => {
      element?.focus();
      element?.setSelectionRange(caret, caret);
    });
  }

  return (
    <Section
      level={3}
      title={template.label}
      // A2 note 4 — a channel is switched off without losing the wording. The
      // switches sit on the heading line, which is `Section`'s action slot.
      action={
        <div className={styles.channels}>
          <Switch
            label="Teams card"
            checked={value.teamsEnabled}
            onChange={(_, data) => onChange({ ...value, teamsEnabled: data.checked })}
          />
          <Switch
            label="Email"
            checked={value.emailEnabled}
            onChange={(_, data) => onChange({ ...value, emailEnabled: data.checked })}
          />
        </div>
      }
    >
      <div className={styles.fields}>
        <Field
          label="Subject"
          validationState={showErrors && subjectError ? 'error' : 'none'}
          validationMessage={showErrors ? subjectError : undefined}
        >
          <Input
            ref={subjectRef}
            value={value.subject}
            onFocus={() => setTarget('subject')}
            onChange={(_, data) => onChange({ ...value, subject: data.value })}
          />
        </Field>

        <Field
          label="Body"
          validationState={showErrors && bodyError ? 'error' : 'none'}
          validationMessage={showErrors ? bodyError : undefined}
        >
          <Textarea
            ref={bodyRef}
            resize="vertical"
            rows={4}
            value={value.body}
            onFocus={() => setTarget('body')}
            onChange={(_, data) => onChange({ ...value, body: data.value })}
          />
        </Field>

        <div className={styles.tokens}>
          <Caption1 className={styles.meta} id={`tokens-${template.name}`}>
            Tokens:
          </Caption1>
          {template.tokens.map((token) => (
            <Tooltip
              key={token.name}
              content={`${token.description} — e.g. ${token.sample}`}
              relationship="description"
              withArrow
            >
              <Button
                size="small"
                appearance="subtle"
                aria-label={`Insert {{${token.name}}} — ${token.description}`}
                onClick={() => insertToken(token.name)}
              >
                {`{{${token.name}}}`}
              </Button>
            </Tooltip>
          ))}
        </div>

        {/* Per template rather than one button for both (the frame draws it in
            the footer): the endpoint tests one template, and note 2 describes
            sending "a card and an email" — singular. A single footer button
            would either have to guess which template was meant or send four
            messages to check one edit. Deliberately NOT the bar's `primary`
            slot: the screen's one emphasised action is "Save templates". */}
        <ActionBar
          secondary={
            <Button appearance="secondary" onClick={onSendTest}>
              {testState.phase === 'sending' ? (
                <Spinner size="tiny" label="Sending…" labelPosition="after" />
              ) : (
                'Send test to me'
              )}
            </Button>
          }
          busy={testState.phase === 'sending'}
        />

        {/* Graph refusing is a 200 carrying the reason, so "sent" is not the same
            as "succeeded" — the result has to be read, not just its status code. */}
        {testState.phase === 'sent' &&
          (testState.result.error === undefined ? (
            <MessageBar intent="success">
              <MessageBarBody>
                {`Test sent with sample values — ${describeChannels(testState.result)}.`}
              </MessageBarBody>
            </MessageBar>
          ) : (
            <MessageBar intent="error">
              <MessageBarBody>
                <MessageBarTitle>Could not send the test</MessageBarTitle>
                {testState.result.error}
              </MessageBarBody>
            </MessageBar>
          ))}

        {testState.phase === 'error' && (
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>Could not send the test</MessageBarTitle>
              {testState.message}
            </MessageBarBody>
          </MessageBar>
        )}
      </div>
    </Section>
  );
}

function describeChannels(result: SendTestNotificationResult): string {
  const channels = [
    result.teamsSent ? 'a Teams card' : undefined,
    result.emailSent ? 'an email' : undefined,
  ].filter((channel) => channel !== undefined);
  return channels.join(' and ');
}

export function NotificationTemplatesSection() {
  const styles = useStyles();
  const layout = useLayoutStyles();
  const responsive = useResponsiveStyles();
  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  const [forms, setForms] = useState<Record<string, NotificationTemplateContent>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [testStates, setTestStates] = useState<Record<string, TestState>>({});
  // K1, sheet 10: submitting with errors moves focus to the error summary,
  // which is role="alert" so it is announced rather than merely rendered.
  const summaryRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setState({ phase: 'loading' });
    try {
      const { templates } = await getNotificationTemplates();
      setState({ phase: 'ready', templates });
      setForms(
        Object.fromEntries(templates.map((template) => [template.name, contentOf(template)])),
      );
    } catch (err) {
      setState({ phase: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const templates = state.phase === 'ready' ? state.templates : [];

  /** Every template whose wording still carries a token the API cannot fill. */
  function invalidTemplates(): { label: string; unknown: string[] }[] {
    return templates.flatMap((template) => {
      const value = forms[template.name];
      if (!value) return [];
      const allowed = template.tokens.map((token) => token.name);
      // Both fields at once — the summary names the template, not the box.
      const unknown = findUnknownTokens(`${value.subject}\n${value.body}`, allowed);
      return unknown.length === 0 ? [] : [{ label: template.label, unknown }];
    });
  }

  function edit(name: NotificationTemplateName, next: NotificationTemplateContent) {
    setSaved(false);
    setForms((current) => ({ ...current, [name]: next }));
  }

  async function handleSave() {
    const invalid = invalidTemplates();
    setShowErrors(true);
    if (invalid.length > 0) {
      // The save is refused here as well as by the API, so a misspelled token
      // never costs a round trip — and the summary is what focus lands on.
      requestAnimationFrame(() => summaryRef.current?.focus());
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      const update = Object.fromEntries(
        templates.map((template) => [template.name, forms[template.name]]),
      ) as NotificationTemplatesUpdate;
      const { templates: stored } = await updateNotificationTemplates(update);
      setState({ phase: 'ready', templates: stored });
      setForms(Object.fromEntries(stored.map((template) => [template.name, contentOf(template)])));
      setSaved(true);
      setShowErrors(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleSendTest(template: NotificationTemplate) {
    const value = forms[template.name];
    if (!value) return;
    setTestStates((current) => ({ ...current, [template.name]: { phase: 'sending' } }));
    try {
      // Deliberately the wording on screen, not what is stored: a test that
      // could only check a saved template would mean shipping it to find out.
      const result = await sendTestNotification({ name: template.name, ...value });
      setTestStates((current) => ({ ...current, [template.name]: { phase: 'sent', result } }));
    } catch (err) {
      setTestStates((current) => ({
        ...current,
        [template.name]: {
          phase: 'error',
          message: err instanceof Error ? err.message : String(err),
        },
      }));
    }
  }

  const invalid = showErrors ? invalidTemplates() : [];

  return (
    <Section
      title="Notification templates"
      description="Three notifications exist: request approved, PR created, and rejection. There is deliberately no submission notification. Tokens are listed under each field and inserted by clicking."
      surface={false}
    >
      <div className={mergeClasses(layout.stack, responsive.touchTargets)}>
        {/* Stated on the screen rather than only in the code: an administrator
            who edits wording and sees no change in a real notification would
            reasonably conclude the save had failed. */}
        <Body1 block className={styles.meta}>
          Saved wording currently drives “Send test to me”. Live notifications still use the shipped
          wording until the send path reads these templates.
        </Body1>

        {state.phase === 'loading' && (
          // Two-second rule: this is a network call on the render path.
          <Spinner label="Loading templates…" labelPosition="after" />
        )}

        {state.phase === 'error' && (
          <>
            <MessageBar intent="error">
              <MessageBarBody>
                <MessageBarTitle>Could not load the notification templates</MessageBarTitle>
                {state.message}
              </MessageBarBody>
            </MessageBar>
            <ActionBar secondary={<Button onClick={() => void load()}>Try again</Button>} />
          </>
        )}

        {templates.map((template) => {
          const value = forms[template.name];
          if (!value) return null;
          return (
            <TemplateEditor
              key={template.name}
              template={template}
              value={value}
              showErrors={showErrors}
              testState={testStates[template.name] ?? { phase: 'idle' }}
              onChange={(next) => edit(template.name, next)}
              onSendTest={() => void handleSendTest(template)}
            />
          );
        })}

        {state.phase === 'ready' && (
          // The Notifications tab's single `appearance="primary"`; every
          // per-template "Send test to me" stays secondary beside it.
          <ActionBar
            primary={{ label: 'Save templates', onClick: () => void handleSave() }}
            busy={saving}
          />
        )}

        {/* Always mounted so focus can move to it, and so a screen reader hears
            the errors appear rather than a region arriving with them already in
            it. Empty until a save is attempted with an unknown token. */}
        {/* Named, because Fluent's own `Field` validation messages are `role=
            "alert"` too — without a name this region is indistinguishable from
            them to a screen reader landing on it. */}
        <div ref={summaryRef} role="alert" aria-label="Template validation errors" tabIndex={-1}>
          {invalid.length > 0 && (
            <MessageBar intent="error">
              <MessageBarBody>
                <MessageBarTitle>These templates were not saved</MessageBarTitle>
                {invalid
                  .map(
                    ({ label, unknown }) =>
                      `${label}: ${unknown.map((token) => `{{${token}}}`).join(', ')}`,
                  )
                  .join(' · ')}
                . Use a token from the list under each field.
              </MessageBarBody>
            </MessageBar>
          )}
        </div>

        {saved && (
          <MessageBar intent="success">
            <MessageBarBody>Notification templates saved.</MessageBarBody>
          </MessageBar>
        )}

        {saveError && (
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>Could not save the notification templates</MessageBarTitle>
              {saveError}
            </MessageBarBody>
          </MessageBar>
        )}
      </div>
    </Section>
  );
}

function contentOf(template: NotificationTemplate): NotificationTemplateContent {
  return {
    subject: template.subject,
    body: template.body,
    teamsEnabled: template.teamsEnabled,
    emailEnabled: template.emailEnabled,
  };
}
